import { create } from 'zustand'
import type { Edge, Node } from '@xyflow/react'
import type { GraphNodeData, JsonValue, ScalarType } from '../types'
import { jsonToGraph, pathToId } from '../lib/jsonToGraph'
import { graphToJson } from '../lib/graphToJson'
import { autoLayout } from '../lib/autoLayout'
import { cleanStringify, tryParse } from '../lib/cleanJson'

type Side = 'json' | 'canvas'

type State = {
  docId: string | null
  jsonText: string
  parseError: string | null
  nodes: Node<GraphNodeData>[]
  edges: Edge[]
  lastEditedBy: Side
  leftPaneWidth: number
  readOnly: boolean
}

type Actions = {
  hydrateFromServer: (docId: string, value: JsonValue, opts?: { readOnly?: boolean }) => void
  reset: () => void
  setJsonText: (text: string) => void
  relayout: () => void
  setLeftPaneWidth: (w: number) => void
  setReadOnly: (ro: boolean) => void

  updateInlineField: (nodeId: string, key: string, nextValue: unknown) => void
  renameInlineKey: (nodeId: string, oldKey: string, newKey: string) => void
  addInlineField: (nodeId: string, key: string) => void
  removeField: (nodeId: string, key: string) => void
  updateScalarValue: (nodeId: string, nextValue: unknown) => void
  toggleCollapsed: (nodeId: string) => void
  toggleHideDescendants: (nodeId: string) => void
  togglePointerCollapsed: (nodeId: string, fieldKey: string) => void
}

const LEFT_PANE_STORAGE_KEY = 'graph-app:left-pane-width'

function loadLeftPaneWidth(): number {
  try {
    const v = window.localStorage.getItem(LEFT_PANE_STORAGE_KEY)
    if (!v) return 480
    const n = parseInt(v, 10)
    return Number.isFinite(n) ? Math.max(240, Math.min(1200, n)) : 480
  } catch {
    return 480
  }
}

export const useGraphStore = create<State & Actions>()((set, get) => ({
  docId: null,
  jsonText: '',
  parseError: null,
  nodes: [],
  edges: [],
  lastEditedBy: 'json',
  leftPaneWidth: loadLeftPaneWidth(),
  readOnly: false,

  hydrateFromServer: (docId, value, opts) => {
    const text = cleanStringify(value)
    const { nodes, edges } = jsonToGraph(value)
    const synced = syncPointerCollapsedFromChildren(nodes)
    const laid = autoLayout(synced, edges)
    const { nodes: visibleNodes, edges: visibleEdges } = applyHidden(laid, edges)
    set({
      docId,
      jsonText: text,
      parseError: null,
      nodes: visibleNodes,
      edges: visibleEdges,
      lastEditedBy: 'json',
      readOnly: opts?.readOnly ?? false,
    })
  },

  reset: () => {
    set({
      docId: null,
      jsonText: '',
      parseError: null,
      nodes: [],
      edges: [],
      lastEditedBy: 'json',
      readOnly: false,
    })
  },

  setJsonText: (text) => {
    if (get().readOnly) return
    const parsed = tryParse(text)
    if (!parsed.ok) {
      set({ jsonText: text, parseError: parsed.error, lastEditedBy: 'json' })
      return
    }
    const prevCollapsed = new Set(
      get().nodes.filter((n) => n.data.collapsed).map((n) => n.id),
    )
    const prevHideDescendants = new Set(
      get().nodes.filter((n) => n.data.hideDescendants).map((n) => n.id),
    )
    const prevPointerCollapsed = new Set<string>()
    for (const n of get().nodes) {
      for (const pf of n.data.pointerFields) {
        if (pf.collapsed) prevPointerCollapsed.add(`${n.id}|${pf.key}`)
      }
    }
    const prevExpanded = new Set<string>()
    for (const n of get().nodes) if (!n.data.collapsed) prevExpanded.add(n.id)
    const { nodes, edges } = jsonToGraph(parsed.value)
    // Preserve collapse state across re-parses. Existing nodes that were
    // expanded stay expanded; newly-added nodes inherit jsonToGraph's
    // default (collapsed for non-root, expanded for root).
    for (const n of nodes) {
      if (prevExpanded.has(n.id)) n.data.collapsed = false
      else if (prevCollapsed.has(n.id)) n.data.collapsed = true
      if (prevHideDescendants.has(n.id)) n.data.hideDescendants = true
    }
    void prevPointerCollapsed
    const synced = syncPointerCollapsedFromChildren(nodes)
    const laid = autoLayout(synced, edges)
    const { nodes: visibleNodes, edges: visibleEdges } = applyHidden(laid, edges)
    set({
      jsonText: text,
      parseError: null,
      nodes: preservePositions(visibleNodes, get().nodes),
      edges: visibleEdges,
      lastEditedBy: 'json',
    })
  },

  relayout: () => {
    const { nodes, edges } = get()
    set({ nodes: autoLayout(nodes, edges) })
  },

  setLeftPaneWidth: (w) => {
    const clamped = Math.max(240, Math.min(1200, w))
    set({ leftPaneWidth: clamped })
    try {
      window.localStorage.setItem(LEFT_PANE_STORAGE_KEY, String(clamped))
    } catch {
      /* ignore */
    }
  },

  setReadOnly: (ro) => set({ readOnly: ro }),

  updateInlineField: (nodeId, key, nextValue) => {
    if (get().readOnly) return
    const nodes = get().nodes.map((n) => {
      if (n.id !== nodeId) return n
      return {
        ...n,
        data: {
          ...n.data,
          inlineFields: n.data.inlineFields.map((f) =>
            f.key === key
              ? {
                  ...f,
                  value: coerceScalar(nextValue, f.valueType),
                  valueType: inferType(nextValue) ?? f.valueType,
                }
              : f,
          ),
        },
      }
    })
    syncToJson(set, nodes)
  },

  renameInlineKey: (nodeId, oldKey, newKey) => {
    if (get().readOnly) return
    if (!newKey || newKey === oldKey) return
    const nodes = get().nodes.map((n) => {
      if (n.id !== nodeId) return n
      if (n.data.inlineFields.some((f) => f.key === newKey)) return n
      return {
        ...n,
        data: {
          ...n.data,
          inlineFields: n.data.inlineFields.map((f) =>
            f.key === oldKey ? { ...f, key: newKey } : f,
          ),
        },
      }
    })
    syncToJson(set, nodes)
  },

  addInlineField: (nodeId, key) => {
    if (get().readOnly) return
    const nodes = get().nodes.map((n) => {
      if (n.id !== nodeId) return n
      if (n.data.inlineFields.some((f) => f.key === key)) return n
      return {
        ...n,
        data: {
          ...n.data,
          inlineFields: [
            ...n.data.inlineFields,
            { key, value: '', valueType: 'string' as ScalarType },
          ],
        },
      }
    })
    syncToJson(set, nodes)
  },

  updateScalarValue: (nodeId, nextValue) => {
    if (get().readOnly) return
    const nodes = get().nodes.map((n) => {
      if (n.id !== nodeId) return n
      const target = n.data.scalarType ?? 'string'
      return {
        ...n,
        data: {
          ...n.data,
          scalarValue: coerceScalar(nextValue, target),
          scalarType: inferType(nextValue) ?? target,
        },
      }
    })
    syncToJson(set, nodes)
  },

  removeField: (nodeId, key) => {
    if (get().readOnly) return
    const nodes = get().nodes.map((n) => {
      if (n.id !== nodeId) return n
      return {
        ...n,
        data: {
          ...n.data,
          inlineFields: n.data.inlineFields.filter((f) => f.key !== key),
          pointerFields: n.data.pointerFields.filter((p) => p.key !== key),
        },
      }
    })
    syncToJson(set, nodes)
  },

  toggleCollapsed: (nodeId) => {
    const state = get()
    const nodes = flipCollapsedCascading(state.nodes, state.edges, [nodeId])
    if (nodes === state.nodes) return
    const synced = syncPointerCollapsedFromChildren(nodes)
    const { nodes: next, edges: nextEdges } = applyHiddenAndRelayout(synced, state.edges)
    set({ nodes: next, edges: nextEdges })
  },

  toggleHideDescendants: (nodeId) => {
    const nodes = get().nodes.map((n) =>
      n.id === nodeId
        ? { ...n, data: { ...n.data, hideDescendants: !n.data.hideDescendants } }
        : n,
    )
    const synced = syncPointerCollapsedFromChildren(nodes)
    const { nodes: next, edges: nextEdges } = applyHiddenAndRelayout(synced, get().edges)
    set({ nodes: next, edges: nextEdges })
  },

  togglePointerCollapsed: (nodeId, fieldKey) => {
    const state = get()
    const parent = state.nodes.find((n) => n.id === nodeId)
    if (!parent) return
    const pf = parent.data.pointerFields.find((p) => p.key === fieldKey)
    if (!pf || pf.childIds.length === 0) return
    const nodes = flipCollapsedCascading(state.nodes, state.edges, pf.childIds)
    if (nodes === state.nodes) return
    const synced = syncPointerCollapsedFromChildren(nodes)
    const { nodes: next, edges: nextEdges } = applyHiddenAndRelayout(synced, state.edges)
    set({ nodes: next, edges: nextEdges })
  },
}))

function syncToJson(
  set: (partial: Partial<State>) => void,
  nodes: Node<GraphNodeData>[],
) {
  const value = graphToJson(nodes)
  const text = cleanStringify(value)
  set({ nodes, jsonText: text, parseError: null, lastEditedBy: 'canvas' })
}


function preservePositions(
  next: Node<GraphNodeData>[],
  prev: Node<GraphNodeData>[],
): Node<GraphNodeData>[] {
  if (prev.length === 0) return next
  const prevPos = new Map(prev.map((n) => [n.id, n.position] as const))
  return next.map((n) => {
    const p = prevPos.get(n.id)
    return p ? { ...n, position: p } : n
  })
}

/**
 * Derive `hidden` on every node/edge via reachability from the root, with
 * "suppressed" edges filtered out. An edge is suppressed when:
 *   - its source node has `hideDescendants` (card-level mass-collapse), OR
 *   - the edge's sourceHandle matches a pointer field with `collapsed` true
 * Ref edges are always kept visible — refs don't block downstream visibility.
 * Anything not reachable from root via non-suppressed structural edges is
 * hidden. The card-level `collapsed` flag is purely visual (body hide) and
 * no longer cascades.
 */
function applyHidden(
  nodes: Node<GraphNodeData>[],
  edges: Edge[],
): { nodes: Node<GraphNodeData>[]; edges: Edge[] } {
  // Only card-level hideDescendants suppresses structural edges now.
  // Per-row chevrons are purely a remote control for the target child's
  // body-collapse, so they do not hide descendant cards themselves.
  const nodeHides = new Set<string>()
  for (const n of nodes) {
    if (n.data.hideDescendants) nodeHides.add(n.id)
  }
  const suppressedEdges = new Set<string>()
  for (const e of edges) {
    if ((e.data as { isRef?: boolean } | undefined)?.isRef) continue
    if (nodeHides.has(e.source)) suppressedEdges.add(e.id)
  }

  // Structural adjacency (non-ref, non-suppressed)
  const outgoing = new Map<string, string[]>()
  for (const e of edges) {
    if ((e.data as { isRef?: boolean } | undefined)?.isRef) continue
    if (suppressedEdges.has(e.id)) continue
    const list = outgoing.get(e.source)
    if (list) list.push(e.target)
    else outgoing.set(e.source, [e.target])
  }

  // BFS reachability from root
  const rootId = '$'
  const reachable = new Set<string>([rootId])
  const stack: string[] = [rootId]
  while (stack.length) {
    const id = stack.pop()!
    const kids = outgoing.get(id)
    if (!kids) continue
    for (const k of kids) {
      if (!reachable.has(k)) {
        reachable.add(k)
        stack.push(k)
      }
    }
  }

  const hidden = new Set<string>()
  for (const n of nodes) if (!reachable.has(n.id)) hidden.add(n.id)

  const nextNodes = nodes.map((n) =>
    hidden.has(n.id) === Boolean(n.hidden) ? n : { ...n, hidden: hidden.has(n.id) },
  )
  const nextEdges = edges.map((e) => {
    const shouldHide = hidden.has(e.source) || hidden.has(e.target)
    return shouldHide === Boolean(e.hidden) ? e : { ...e, hidden: shouldHide }
  })
  return { nodes: nextNodes, edges: nextEdges }
}

/**
 * Flip collapsed state on a list of target node ids with asymmetric cascade:
 *   - Collapsing a card also collapses every structural descendant.
 *   - Expanding a card only expands that card; its descendants stay as they
 *     were so you can drill back in layer by layer.
 * The decision (collapse vs expand) is taken per-target based on its current
 * state. When called with a group of targets (e.g. all items in an array),
 * the first target's state drives the chosen direction for the whole group.
 */
function flipCollapsedCascading(
  nodes: Node<GraphNodeData>[],
  edges: Edge[],
  targetIds: string[],
): Node<GraphNodeData>[] {
  if (targetIds.length === 0) return nodes
  const byId = new Map(nodes.map((n) => [n.id, n] as const))
  const first = byId.get(targetIds[0])
  if (!first) return nodes
  const nextCollapsed = !first.data.collapsed
  const affected = new Set<string>()
  for (const tid of targetIds) {
    affected.add(tid)
    if (nextCollapsed) {
      for (const d of structuralDescendants(nodes, edges, tid)) affected.add(d)
    }
  }
  return nodes.map((n) =>
    affected.has(n.id) ? { ...n, data: { ...n.data, collapsed: nextCollapsed } } : n,
  )
}

/**
 * Mirror each pointer field's `collapsed` flag from the body-collapse state
 * of the first child it points to. Used only for rendering — the pointer-
 * row chevron shows where the child will land when you click it.
 */
function syncPointerCollapsedFromChildren(
  nodes: Node<GraphNodeData>[],
): Node<GraphNodeData>[] {
  const byId = new Map(nodes.map((n) => [n.id, n] as const))
  return nodes.map((n) => {
    let changed = false
    const newPointers = n.data.pointerFields.map((pf) => {
      if (pf.childIds.length === 0) return pf
      const firstChild = byId.get(pf.childIds[0])
      if (!firstChild) return pf
      const childCollapsed = firstChild.data.collapsed
      if (pf.collapsed === childCollapsed) return pf
      changed = true
      return { ...pf, collapsed: childCollapsed }
    })
    if (!changed) return n
    return { ...n, data: { ...n.data, pointerFields: newPointers } }
  })
}

/**
 * BFS through structural (non-ref) edges from `rootId`, returning every
 * reachable descendant id.
 */
function structuralDescendants(
  nodes: Node<GraphNodeData>[],
  edges: Edge[],
  rootId: string,
): Set<string> {
  void nodes
  const outgoing = new Map<string, string[]>()
  for (const e of edges) {
    if ((e.data as { isRef?: boolean } | undefined)?.isRef) continue
    const list = outgoing.get(e.source)
    if (list) list.push(e.target)
    else outgoing.set(e.source, [e.target])
  }
  const out = new Set<string>()
  const stack = [...(outgoing.get(rootId) ?? [])]
  while (stack.length) {
    const id = stack.pop()!
    if (out.has(id)) continue
    out.add(id)
    const kids = outgoing.get(id)
    if (kids) stack.push(...kids)
  }
  return out
}

/**
 * Mark hidden, then run Dagre on the VISIBLE subset only so the remaining
 * cards re-flow to fill the freed space. Hidden nodes keep stale positions
 * (they're not rendered anyway) until they become visible again and get
 * re-laid-out on the next toggle.
 */
function applyHiddenAndRelayout(
  nodes: Node<GraphNodeData>[],
  edges: Edge[],
): { nodes: Node<GraphNodeData>[]; edges: Edge[] } {
  const { nodes: marked, edges: markedEdges } = applyHidden(nodes, edges)
  const visibleNodes = marked.filter((n) => !n.hidden)
  const visibleEdges = markedEdges.filter((e) => !e.hidden)
  const laid = autoLayout(visibleNodes, visibleEdges)
  const positionMap = new Map(laid.map((n) => [n.id, n.position] as const))
  return {
    nodes: marked.map((n) => {
      const p = positionMap.get(n.id)
      return p ? { ...n, position: p } : n
    }),
    edges: markedEdges,
  }
}

function inferType(v: unknown): ScalarType | undefined {
  if (v === null) return 'null'
  if (typeof v === 'boolean') return 'boolean'
  if (typeof v === 'number' && Number.isFinite(v)) return 'number'
  if (typeof v === 'string') return 'string'
  return undefined
}

function coerceScalar(v: unknown, target: ScalarType): string | number | boolean | null {
  if (v === null || v === undefined) return null
  if (target === 'number') {
    const n = typeof v === 'number' ? v : parseFloat(String(v))
    return Number.isFinite(n) ? n : 0
  }
  if (target === 'boolean') return Boolean(v)
  if (target === 'null') return null
  return String(v)
}

export { pathToId }
